import { createAsyncThunk } from "@reduxjs/toolkit";
import { DeliveryMethodType } from "../slices/delivery.slice";

export interface DeliveryPoint {
  servicePointId: string;
  name: string;
  deliveryAddress: {
    streetName: string;
    streetNumber: string;
    postalCode: string;
    city: string;
  };
}

export const validateAddress = createAsyncThunk(
  "delivery/validateAddress",
  async (data: { streetName: string; postalCode: string; city: string }) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/delivery/validate-address`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to validate address");
    }

    return response.json();
  }
);

export const fetchServicePoints = createAsyncThunk(
  "delivery/fetchServicePoints",
  async (data: { streetName: string; postalCode: string; city: string }) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/delivery/service-points`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch service points");
    }

    return response.json() as Promise<DeliveryPoint[]>;
  }
);

export const fetchParcelBoxes = createAsyncThunk(
  "delivery/fetchParcelBoxes",
  async (data: { streetName: string; postalCode: string; city: string }) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/delivery/parcel-boxes`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch parcel boxes");
    }

    return response.json() as Promise<DeliveryPoint[]>;
  }
);

export const createDeliveryAddress = createAsyncThunk(
  "delivery/createDeliveryAddress",
  async (data: {
    orderId: string;
    firstName: string;
    lastName: string;
    street: string;
    city: string;
    postalCode: string;
    phone: string;
    setAsBilling?: boolean;
  }) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/delivery/create-address`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to create delivery address");
    }

    return response.json();
  }
);

export const fetchShippingMethods = createAsyncThunk(
  "delivery/fetchShippingMethods",
  async (orderId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/delivery/shipping-methods/${orderId}`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch shipping methods");
    }

    return response.json();
  }
);

export const fetchSelectDeliveryMethod = createAsyncThunk(
  "delivery/selectDeliveryMethod",
  async (data: {
    orderId: string;
    shipmentId: string;
    shippingMethodId: string;
    deliveryMethod: DeliveryMethodType;
    metadata: Record<string, string>;
  }) => {
    console.log("Selecting delivery method with data:", data);
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/delivery/select-method`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to select delivery method");
    }

    return response.json();
  }
);
